import { Aes256Gcm, CipherSuite, DhkemP256HkdfSha256, HkdfSha256 } from '@hpke/core';
import { argon2id } from 'hash-wasm';
import { ARGON2ID_PARAMETERS, type AccountBundleV1, type Argon2idParametersV1 } from './types';
import { asBytes, base64url, canonicalBytes, concat, unbase64url, utf8, type Binary } from './encoding';

const cryptoApi = globalThis.crypto;
if (!cryptoApi?.subtle) throw new Error('WebCrypto ist für HoldThat E2EE erforderlich.');

export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return cryptoApi.getRandomValues(new Uint8Array(length));
}

export async function sha256(value: Binary): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await cryptoApi.subtle.digest('SHA-256', asBytes(value)));
}

export async function aesGcmEncrypt(keyBytes: Binary, plaintext: Binary, aad: Binary, nonce = randomBytes(12)) {
  const key = await cryptoApi.subtle.importKey('raw', asBytes(keyBytes), { name: 'AES-GCM' }, false, ['encrypt']);
  const ciphertext = await cryptoApi.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: asBytes(aad), tagLength: 128 }, key, asBytes(plaintext));
  return { nonce, ciphertext: new Uint8Array(ciphertext) };
}

export async function aesGcmDecrypt(keyBytes: Binary, nonce: Binary, ciphertext: Binary, aad: Binary) {
  if (asBytes(keyBytes).length !== 32 || asBytes(nonce).length !== 12 || asBytes(ciphertext).length < 16) throw new Error('Ungültige AES-256-GCM-Größe.');
  const key = await cryptoApi.subtle.importKey('raw', asBytes(keyBytes), { name: 'AES-GCM' }, false, ['decrypt']);
  return new Uint8Array(await cryptoApi.subtle.decrypt({ name: 'AES-GCM', iv: asBytes(nonce), additionalData: asBytes(aad), tagLength: 128 }, key, asBytes(ciphertext)));
}

export async function hkdf(rootKey: Binary, info: string, salt: Binary = new Uint8Array()): Promise<Uint8Array<ArrayBuffer>> {
  const source = await cryptoApi.subtle.importKey('raw', asBytes(rootKey), 'HKDF', false, ['deriveBits']);
  const bits = await cryptoApi.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: asBytes(salt), info: utf8(info) }, source, 256);
  return new Uint8Array(bits);
}

function p256PublicJwk(publicRaw: Uint8Array): JsonWebKey {
  if (publicRaw.length !== 65 || publicRaw[0] !== 4) throw new Error('P-256 Public Key muss 65 Byte SEC1 unkomprimiert sein.');
  return { kty: 'EC', crv: 'P-256', x: base64url(publicRaw.slice(1, 33)), y: base64url(publicRaw.slice(33, 65)), ext: true };
}

function p256PrivateJwk(privateRaw: Uint8Array, publicRaw: Uint8Array): JsonWebKey {
  if (privateRaw.length !== 32) throw new Error('P-256 Private Key muss 32 Byte lang sein.');
  return { ...p256PublicJwk(publicRaw), d: base64url(privateRaw) };
}

export async function generateP256Pair(usage: 'ECDH' | 'ECDSA'): Promise<{ privateKey: Uint8Array; publicKey: Uint8Array }> {
  const pair = await cryptoApi.subtle.generateKey({ name: usage, namedCurve: 'P-256' }, true, usage === 'ECDSA' ? ['sign', 'verify'] : ['deriveBits']);
  const jwk = await cryptoApi.subtle.exportKey('jwk', pair.privateKey);
  const raw = new Uint8Array(await cryptoApi.subtle.exportKey('raw', pair.publicKey));
  if (!jwk.d) throw new Error('P-256 Private Key konnte nicht exportiert werden.');
  return { privateKey: unbase64url(jwk.d, 32), publicKey: raw };
}

export async function createAccountBundle(keyRevision = 1): Promise<AccountBundleV1> {
  const encryption = await generateP256Pair('ECDH');
  const signing = await generateP256Pair('ECDSA');
  return {
    schemaVersion: 1, keyRevision, accountRootKey: base64url(randomBytes(32)),
    encryptionPrivateKey: base64url(encryption.privateKey), encryptionPublicKey: base64url(encryption.publicKey),
    signingPrivateKey: base64url(signing.privateKey), signingPublicKey: base64url(signing.publicKey),
  };
}

export async function sign(bundle: AccountBundleV1, value: Record<string, unknown>): Promise<string> {
  const key = await cryptoApi.subtle.importKey('jwk', p256PrivateJwk(unbase64url(bundle.signingPrivateKey, 32), unbase64url(bundle.signingPublicKey, 65)), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign']);
  const signature = new Uint8Array(await cryptoApi.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key, canonicalBytes(value)));
  if (signature.length !== 64) throw new Error('ECDSA-Signatur muss 64 Byte r || s enthalten.');
  return base64url(signature);
}

export async function verify(publicKey: string, value: Record<string, unknown>, signature: string): Promise<boolean> {
  const key = await cryptoApi.subtle.importKey('jwk', p256PublicJwk(unbase64url(publicKey, 65)), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  return cryptoApi.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, unbase64url(signature, 64), canonicalBytes(value));
}

export async function fingerprint(encryptionPublicKey: string, signingPublicKey: string): Promise<string> {
  return base64url(await sha256(canonicalBytes({ cryptoVersion: 1, encryptionPublicKey, signingPublicKey })));
}

export async function derivePassphraseKey(passphrase: string, parameters: Argon2idParametersV1): Promise<Uint8Array> {
  if (parameters.memoryKiB !== ARGON2ID_PARAMETERS.memoryKiB || parameters.iterations !== 3 || parameters.parallelism !== 4 || parameters.outputBytes !== 32) {
    throw new Error('Nicht unterstützte Argon2id-Parameter.');
  }
  if (typeof Worker !== 'undefined' && typeof window !== 'undefined') {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('./argon2.worker.ts', import.meta.url), { type: 'module', name: 'holdthat-argon2id' });
      worker.onmessage = (event: MessageEvent<{ key?: ArrayBuffer; error?: string }>) => {
        worker.terminate();
        if (event.data.error) reject(new Error(event.data.error));
        else if (event.data.key) resolve(new Uint8Array(event.data.key));
        else reject(new Error('Argon2id-Worker lieferte kein Ergebnis.'));
      };
      worker.onerror = (event) => { worker.terminate(); reject(new Error(event.message)); };
      worker.postMessage({ passphrase: passphrase.normalize('NFKC'), salt: parameters.salt });
    });
  }
  const result = await argon2id({ password: passphrase.normalize('NFKC'), salt: unbase64url(parameters.salt, 16), parallelism: 4, iterations: 3, memorySize: 65_536, hashLength: 32, outputType: 'binary' });
  return new Uint8Array(result);
}

const hpkeSuite = new CipherSuite({ kem: new DhkemP256HkdfSha256(), kdf: new HkdfSha256(), aead: new Aes256Gcm() });

export async function hpkeSeal(publicKeyRaw: string, plaintext: Binary, info: string, aad: Binary) {
  const recipientPublicKey = await hpkeSuite.kem.importKey('raw', unbase64url(publicKeyRaw, 65).buffer, true);
  const sender = await hpkeSuite.createSenderContext({ recipientPublicKey, info: utf8(info) });
  const ciphertext = new Uint8Array(await sender.seal(asBytes(plaintext), asBytes(aad)));
  return { encapsulatedKey: base64url(sender.enc), ciphertext: base64url(ciphertext) };
}

export async function hpkeOpen(bundle: AccountBundleV1, encapsulatedKey: string, ciphertext: string, info: string, aad: Binary) {
  const recipientKey = await hpkeSuite.kem.importKey('jwk', p256PrivateJwk(unbase64url(bundle.encryptionPrivateKey, 32), unbase64url(bundle.encryptionPublicKey, 65)), false);
  const recipient = await hpkeSuite.createRecipientContext({ recipientKey, enc: unbase64url(encapsulatedKey, 65), info: utf8(info) });
  return new Uint8Array(await recipient.open(unbase64url(ciphertext), asBytes(aad)));
}

export function bundleBytes(bundle: AccountBundleV1): Uint8Array<ArrayBuffer> { return canonicalBytes(bundle); }
export function assertBundle(bundle: AccountBundleV1): AccountBundleV1 {
  if (bundle.schemaVersion !== 1 || !Number.isSafeInteger(bundle.keyRevision) || bundle.keyRevision < 1) throw new Error('Nicht unterstütztes Account-Bundle.');
  unbase64url(bundle.accountRootKey, 32); unbase64url(bundle.encryptionPrivateKey, 32); unbase64url(bundle.encryptionPublicKey, 65);
  unbase64url(bundle.signingPrivateKey, 32); unbase64url(bundle.signingPublicKey, 65);
  return bundle;
}

export function blockNonce(prefix: Binary, index: number): Uint8Array<ArrayBuffer> {
  const head = asBytes(prefix); if (head.length !== 8 || index < 0 || index > 0xffffffff) throw new Error('Ungültiger Attachment-Blocknonce.');
  const tail = new Uint8Array(4); new DataView(tail.buffer).setUint32(0, index, false); return concat(head, tail);
}
